import React, { useCallback, useEffect, useMemo, useState } from "react";
import QRCode from "qrcode";
import { useTranslation } from "react-i18next";
import { buildZelleQrUrl, normalizeZelleToken } from "../services/Zelle";

/**
 * Renders Zelle payout QR (primary). Copy token is a fallback when a bank app won't scan from the photo library.
 */
export default function ZelleQrBlock({ token, recipientName }) {
  const { t } = useTranslation();
  const copyValue = useMemo(() => normalizeZelleToken(token), [token]);
  const payloadUrl = useMemo(
    () => buildZelleQrUrl({ token, name: recipientName }),
    [token, recipientName]
  );
  const [dataUrl, setDataUrl] = useState("");
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!payloadUrl) {
      setDataUrl("");
      setLoading(false);
      setError(true);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(false);
    QRCode.toDataURL(payloadUrl, {
      width: 260,
      margin: 2,
      errorCorrectionLevel: "M",
      color: { dark: "#1e1b4b", light: "#ffffff" },
    })
      .then((u) => {
        if (!cancelled) setDataUrl(u);
      })
      .catch(() => {
        if (!cancelled) {
          setDataUrl("");
          setError(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [payloadUrl]);

  const copyZelleUsername = useCallback(async () => {
    if (!copyValue) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(copyValue);
      } else {
        const ta = document.createElement("textarea");
        ta.value = copyValue;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore
    }
  }, [copyValue]);

  if (!payloadUrl) return null;

  return (
    <div className="border rounded-3 p-3 mt-3 as-zelle-qr-block">
      <p className="text-muted small mb-2">{t("general.zelle_qr_instructions")}</p>

      <div className="d-flex flex-column align-items-center gap-3">
        {loading ? (
          <div className="text-muted small py-4">{t("general.zelle_qr_loading")}</div>
        ) : error || !dataUrl ? (
          <div className="text-danger small">{t("general.zelle_qr_error")}</div>
        ) : (
          <img
            src={dataUrl}
            alt=""
            width={260}
            height={260}
            className="rounded border bg-white"
            decoding="async"
          />
        )}
        {copyValue ? (
          <div className="w-100 pt-3 mt-1 border-top" style={{ maxWidth: 280 }}>
            <p className="text-muted small mb-2">{t("general.zelle_qr_copy_hint")}</p>
            <div
              className="font-monospace small text-break bg-light border rounded px-2 py-2 mb-2 text-center"
              style={{ wordBreak: "break-all" }}
            >
              {copyValue}
            </div>
            <button type="button" className="btn btn-sm as-btn-soft w-100" onClick={copyZelleUsername}>
              {copied ? t("general.copied") : t("general.copy_zelle_username")}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
